import React, { useState, useEffect, useMemo } from "react";
import "./history.scss";
import Cookies from "js-cookie";
import { updateUserToken } from "@/utils/api/services/auth.services";
import { eventRoomStore } from "../../../../stores/event";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faHistory,
  faMapMarkerAlt,
  faClock,
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faSpinner,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";

export type VisitStatus = "completed" | "cancelled" | "no_show";

export interface Visit {
  id: string | number;
  visit_name: string;
  residence_name: string;
  time: string;
  time_raw: string;
  duration: number | null;
  status: VisitStatus;
  guide_name?: string;
}

const getMonthOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [
    { value: "", label: "All" },
  ];
  const now = new Date();
  const start = new Date(2026, 0, 1); // January 2026

  const current = new Date(now.getFullYear(), now.getMonth(), 1);
  while (current >= start) {
    const year = current.getFullYear();
    const month = current.getMonth();
    const value = `${year}-${String(month + 1).padStart(2, "0")}`;
    const label = current.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    options.push({ value, label });
    current.setMonth(current.getMonth() - 1);
  }

  return options;
};

const VisitHistory: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  const monthOptions = useMemo(() => getMonthOptions(), []);

  const fetchHistory = async () => {
    if (hasLoaded) return;

    setIsLoading(true);
    try {
      const result = await eventRoomStore.fetchVisitsHistory();
      if (result.status === 200 && result.history) {
        setVisits(result.history.entries || []);
        setTotal(result.history.total || 0);
        setHasLoaded(true);
      } else if (result.status === 401) {
        await updateUserToken(Cookies.get("token"));
        fetchHistory();
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!isExpanded && !hasLoaded) {
      fetchHistory();
    }
    setIsExpanded(!isExpanded);
  };

  const filteredVisits = useMemo(() => {
    return [...visits]
      .filter((v) => v.status === "completed" || v.status === "no_show")
      .filter((v) => !selectedMonth || v.time_raw.startsWith(selectedMonth))
      .sort(
        (a, b) =>
          new Date(b.time_raw).getTime() - new Date(a.time_raw).getTime()
      );
  }, [visits, selectedMonth]);

  const formatDuration = (minutes: number | null): string => {
    if (!minutes) return "-";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getStatusIcon = (status: VisitStatus) => {
    switch (status) {
      case "completed":
        return faCheckCircle;
      case "no_show":
        return faExclamationTriangle;
      default:
        return faCheckCircle;
    }
  };

  const getStatusLabel = (status: VisitStatus) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "no_show":
        return "No show";
      default:
        return status;
    }
  };

  return (
    <div className="travel-me-history-root">
      {/* HEADER TOGGLE */}
      <header className="history-header" onClick={toggleHistory}>
        <div className="history-header-content">
          <div className="history-icon-wrapper">
            <FontAwesomeIcon icon={faHistory} />
          </div>
          <h3 className="history-title">
            Visit History
            {hasLoaded && filteredVisits.length > 0 && (
              <span className="history-count">{filteredVisits.length} visits</span>
            )}
          </h3>
        </div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`chevron-icon ${isExpanded ? "is-active" : ""}`}
        />
      </header>

      {/* COLLAPSIBLE CONTENT */}
      <div
        className={`history-expandable-grid ${isExpanded ? "is-open" : ""}`}
      >
        <div className="history-content-inner">
          {/* Month filter */}
          {hasLoaded && (
            <div className="history-filter-bar">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select
                className="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="history-list">
            {isLoading ? (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} className="spinner" spin />
                <p>Loading history...</p>
              </div>
            ) : filteredVisits.length > 0 ? (
              filteredVisits.map((visit) => (
                <article key={visit.id} className="visit-card">
                  <div className="visit-card-header">
                    <div className="visit-info-group">
                      <span className="visit-name">{visit.visit_name}</span>
                      <div className="residence-name">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        {visit.residence_name || "Residence"}
                      </div>
                    </div>
                    <div className={`status-badge ${visit.status}`}>
                      <FontAwesomeIcon icon={getStatusIcon(visit.status)} />
                      {getStatusLabel(visit.status)}
                    </div>
                  </div>

                  <div className="visit-meta-footer">
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {visit.time}
                    </div>
                    {visit.duration && (
                      <div className="meta-item">
                        <FontAwesomeIcon icon={faClock} />
                        {formatDuration(visit.duration)}
                      </div>
                    )}
                  </div>
                </article>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </div>
                <p className="empty-title">No history</p>
                <p className="empty-text">
                  No visits found for this period.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitHistory;
